/**
 * CampaignCard Component
 * 
 * Displays a campaign within a store with its tasks
 */

import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { memo, useMemo } from 'react';
import { ChevronDown, ChevronUp, Package, CheckCircle2, AlertCircle, Clock, Image as ImageIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CampaignAtStore, PackTask } from '@/types/store-first';
import { useAssignmentEvidence } from '@/hooks/useAssignmentEvidence';

interface CampaignCardProps {
  campaign: CampaignAtStore;
  isExpanded?: boolean;
  onToggle?: () => void;
  onPackTaskClick?: (packTask: PackTask, campaign: CampaignAtStore) => void;
}

export const CampaignCard = memo(function CampaignCard({ campaign, isExpanded = false, onToggle, onPackTaskClick }: CampaignCardProps) {
  const statusConfig = {
    pending: {
      icon: Clock,
      label: 'Pending',
      color: 'bg-gray-100 text-gray-700',
      iconColor: 'text-gray-500'
    },
    in_progress: {
      icon: Clock,
      label: 'In Progress',
      color: 'bg-blue-100 text-blue-700',
      iconColor: 'text-blue-500'
    },
    completed: {
      icon: CheckCircle2,
      label: 'Completed',
      color: 'bg-green-100 text-green-700',
      iconColor: 'text-green-500'
    },
    issue: {
      icon: AlertCircle,
      label: 'Issue',
      color: 'bg-red-100 text-red-700',
      iconColor: 'text-red-500'
    }
  };

  const packTask = campaign.packTask;
  if (!packTask) {
    // Log error but still render campaign card (maybe with a warning)
    console.error('[CampaignCard] Missing packTask for campaign:', campaign.campaignId);
    return (
      <Card className="mb-3 border-red-300">
        <CardContent className="p-4">
          <div className="text-red-600 text-sm">
            ⚠️ Campaign data incomplete. Please refresh the page.
          </div>
        </CardContent>
      </Card>
    );
  }

  // Use packTask.status for badge (it reflects actual completion state)
  // Fall back to campaign.status if packTask.status is not available
  const effectiveStatus = packTask.status || campaign.status;
  
  const fallbackTaskId = (packTask as unknown as { taskId?: number }).taskId;
  const displayedAssignmentId = packTask.assignmentId ?? packTask.legacyAssignmentId ?? fallbackTaskId ?? 'N/A';

  // Debug logging to see what status values we're getting
  console.log('[CampaignCard] Status debug', JSON.stringify({
    campaignId: campaign.campaignId,
    campaignStatus: campaign.status,
    packTaskStatus: packTask.status,
    effectiveStatus,
    assignmentId: packTask.assignmentId,
    legacyAssignmentId: packTask.legacyAssignmentId,
    fallbackTaskId
  }));

  console.log('[CampaignCard] packTask raw', JSON.stringify(packTask));
  
  // Map database status values to UI status values
  // Database uses: 'pending', 'operator_complete', 'admin_complete', 'locked', 'completed'
  // UI expects: 'pending', 'in_progress', 'completed', 'issue'
  const mapStatusToUI = (dbStatus: string): 'pending' | 'in_progress' | 'completed' | 'issue' => {
    if (dbStatus === 'operator_complete' || dbStatus === 'admin_complete' || dbStatus === 'completed') {
      return 'completed';
    }
    if (dbStatus === 'in_progress') {
      return 'in_progress';
    }
    if (dbStatus === 'issue') {
      return 'issue';
    }
    return 'pending';
  };
  
  const uiStatus = mapStatusToUI(effectiveStatus);
  const config = statusConfig[uiStatus] || statusConfig.pending;
  const StatusIcon = config.icon;

  const assignmentCandidates = useMemo(
    () => [packTask.assignmentId, packTask.realAssignmentId, packTask.legacyAssignmentId],
    [packTask.assignmentId, packTask.realAssignmentId, packTask.legacyAssignmentId]
  );

  const { data: evidenceData, error: evidenceError } = useAssignmentEvidence(
    uiStatus === 'completed' ? assignmentCandidates : []
  );

  const evidenceFormatMap = useMemo(() => {
    if (!evidenceData?.formatEvidence) return null;
    const map = new Map<number, { actual: number; expected: number }>();
    evidenceData.formatEvidence.forEach((fe) => {
      map.set(fe.formatId, {
        actual: fe.actualCount ?? 0,
        expected: fe.expectedCount ?? 0,
      });
    });
    return map;
  }, [evidenceData]);

  const deliveredQuantityFromEvidence = useMemo(() => {
    if (!evidenceFormatMap) return null;
    let total = 0;
    evidenceFormatMap.forEach(({ actual }) => {
      total += actual || 0;
    });
    return total;
  }, [evidenceFormatMap]);

  const plannedQuantityFromEvidence = evidenceData?.assignment?.plannedQuantity ?? null;

  const completedFormats = evidenceFormatMap
    ? Array.from(evidenceFormatMap.values()).filter(({ actual }) => actual > 0).length
    : packTask.formats?.filter(f => f.deliveredQuantity > 0).length || 0;
  const totalFormats = packTask.formats?.length || 0;
  const effectivePlannedQuantity = plannedQuantityFromEvidence ?? packTask.plannedQuantity;
  const effectiveDeliveredQuantity = deliveredQuantityFromEvidence ?? packTask.deliveredQuantity;
  const progressPercent = effectivePlannedQuantity > 0
    ? (effectiveDeliveredQuantity / effectivePlannedQuantity) * 100
    : 0;

  if (evidenceError) {
    console.warn('[CampaignCard] Evidence fetch failed', {
      assignmentCandidates,
      error: evidenceError instanceof Error ? evidenceError.message : evidenceError,
    });
  }

  console.log('[CampaignCard] assignment info', JSON.stringify({
    campaignId: campaign.campaignId,
    assignmentId: packTask.assignmentId,
    legacyAssignmentId: packTask.legacyAssignmentId,
    fallbackTaskId,
    displayedAssignmentId
  }));

  return (
    <Card className="mb-3">
      <CardHeader 
        className="cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-semibold text-lg">{campaign.campaignName}</h3>
              <Badge className={cn(config.color, "flex items-center gap-1")}>
                <StatusIcon className={cn("h-3 w-3", config.iconColor)} />
                {config.label}
              </Badge>
            </div>
            <div className="text-sm font-semibold text-blue-700 mb-2">
              Assignment ID: {displayedAssignmentId}
            </div>
            
            {campaign.packName && (
              <div className="flex items-center gap-1 text-sm text-gray-600 mb-2">
                <Package className="h-4 w-4" />
                <span>{campaign.packName}</span>
              </div>
            )}

            {/* Pack-level image indicator */}
            {campaign.imageUrl && (
              <div className="flex items-center gap-1 text-sm text-blue-600 mb-2">
                <ImageIcon className="h-4 w-4" />
                <span>Reference image available</span>
              </div>
            )}

            <div className="text-sm text-gray-600">
              {totalFormats} {totalFormats === 1 ? 'format' : 'formats'} in pack
            </div>
          </div>

          {onToggle && (
            <Button
              variant="ghost"
              size="sm"
              className="ml-2"
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
            >
              {isExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>

        {/* Progress bar */}
        {effectivePlannedQuantity > 0 && (
          <div className="mt-3">
            <div className="flex justify-between text-xs text-gray-600 mb-1">
              <span>Pack progress</span>
              <span>{effectiveDeliveredQuantity} / {effectivePlannedQuantity}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div
                className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        )}
      </CardHeader>

      {isExpanded && (
        <CardContent className="pt-0">
          <div className="space-y-2">
            {/* Show formats for reference */}
            {packTask.formats && packTask.formats.length > 0 ? (
              packTask.formats.map((format) => (
                <div
                  key={format.formatId}
                  className="p-2 rounded border bg-gray-50 text-sm"
                >
                  <div className="font-medium">{format.formatName || `Format ${format.formatId}`}</div>
                  <div className="text-xs text-gray-600">
                    {(() => {
                      const evidenceFormat = evidenceFormatMap?.get(format.formatId);
                      const planned = evidenceFormat?.expected ?? format.plannedQuantity;
                      const delivered = evidenceFormat?.actual ?? format.deliveredQuantity;
                      return `Planned: ${planned} | Delivered: ${delivered}`;
                    })()}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">No formats in this pack</p>
            )}
            
            {/* Pack task completion button */}
            {onPackTaskClick && (
              <Button
                onClick={() => onPackTaskClick(packTask, campaign)}
                className="w-full mt-3"
                variant={uiStatus === 'completed' || uiStatus === 'issue' ? 'outline' : 'default'}
              >
                {uiStatus === 'completed' || uiStatus === 'issue' ? 'View Evidence' : 'Complete Pack'}
              </Button>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
});

